import { Path } from "../imgtrace/svg";
import {
    Point,
    LineElement,
    CanvasElement,
    ResetAnimationEnum,
    AnimationHelper,
    ShapeStyle,
    ImgUtils,
    Rect,
    MathX,
    ClassAnimation,
    SizedCanvasElement
} from "mini-canvas-engine";
import { DotElement, DotHitTargetType } from "./elements/dot-element";
import { Shape } from "../api/dots-game-api";
import {
    GAME_LINE_SHAPE_STYLE,
    DOT_HIT_EXPANSION_TOUCH,
    IMG_STYLES_ROOT,
    DOT_HIT_EXPANSION_TOUCH_MIN,
    dotRadiusForFont,
    DOT_HIT_EXPANSION_MOUSE_MIN,
    IMG_GAME_ROOT,
    GAME_STYLES,
    GameStyles,
    QuizMode,
    BRASS_FAIL_SOUNDS
} from "./game-consts";
import { DotsBasePainter, DotsPainter } from "./dots-painter";
import { Utils } from "../utils/utils";
import { BaseGameController, Game } from "../game-base/base-game-controller";
import { AnimDotStatus } from "./game-types";
import { Firework } from "./effects/fireworks";
import { QuizQuestionElement } from "./elements/quiz-question-element";

const MAX_ZOOM_QUIZ_ANSWER = 5;

export class DotsConnector extends DotsBasePainter implements Game {
    private imageSrc = "";

    private linesCompleted = -1;

    private readonly magnifierScale = 1.1;

    onSolvedStep: () => void;
    onGameOverAnimFinished: () => void;

    lineStyle: ShapeStyle;
    hintImageSrc?: string;
    boardOriginBeforeVibrate: Point;
    controller: BaseGameController;

    // Quiz
    quizQuestionElement: QuizQuestionElement;

    constructor(boardElement: HTMLElement, private magnifierCanvas?: HTMLCanvasElement, forcePixelRatio?: number) {
        super(0, 0, boardElement, forcePixelRatio);

        window.addEventListener("resize", () => this.windowResize());

        if (magnifierCanvas && this.board.isTouchDevice) {
            this.controller.initializeMagnifier(magnifierCanvas, this.getMagnifierSize(), this.magnifierScale, this.style?.magnifierColor);
        }
    }

    private getMagnifierSize(): number {
        const boardWidth = this.boardElement?.clientWidth ?? 320;
        const doubleMaxDistance = this.getDoubleMaxDistance((boardWidth / 3) * this.board.pixelRatio);
        return (doubleMaxDistance + dotRadiusForFont(this.dotFontSize) * 6) * this.magnifierScale;
    }

    public get gameCompletion(): number {
        return this.linesCompleted;
    }

    public get revealed(): number | undefined {
        return undefined;
    }

    public get canZoomInAndOut(): boolean {
        return true;
    }

    setupBoard() {
        super.setupBoard();
        if (!this.boardElement) {
            throw new Error("Unexpected empty board element");
        }
        this.board.onMouseDown = (pos: Point, _e: MouseEvent, hit: CanvasElement | undefined) => this.mouseDown(pos, hit);
        this.board.onMouseMove = (pos: Point) => this.mouseMove(pos);
        this.board.onMouseUp = () => this.mouseUp();

        this.controller = new BaseGameController(this.board);
        this.controller.setupBoard();
    }

    setupDrawLayer() {
        super.setupDrawLayer();

        this.controller.addEffectLayerAbove();
    }

    windowResize(): any {
        if (!this.imageSrc || !this.dots) {
            return;
        }

        // to do resize
        // this.board.resize();
    }

    // public initialize(imageSrc: string, path: Path): Promise<void> {
    //     return this.initializeFromPoints(imageSrc, this.getDrawingPoints(path));
    // }

    public async initializeFromPoints(imageSrc: string, shape: Shape, hintImageSrc?: string, startGame = true): Promise<void> {
        this.initializeDotsFromShape(shape);

        this.imageSrc = imageSrc;
        this.linesCompleted = -1;
        this.hintImageSrc = hintImageSrc;

        await this.setupGame();
        if (startGame) {
            this.start();
        }
    }

    private initializeQuiz() {
        if (this._isQuiz) {
            if (!this.quizQuestionElement) {
                this.quizQuestionElement = new QuizQuestionElement(this.board, this.questions);
                this.board.layers[this.board.layers.length - 1].add(this.quizQuestionElement);
                QuizQuestionElement.loadSounds();
            } else {
                this.quizQuestionElement.hide();
            }
        } else {
            if (this.quizQuestionElement) {
                this.quizQuestionElement.hide();
            }
        }
    }

    getDrawingPoints(path: Path): Point[] {
        return path.commands.filter((c) => c.point).map((c) => new Point(c.value[0], c.value[1]));
    }

    private async setupGame(): Promise<void> {
        if (!this.imageSrc || !this.dots) {
            return;
        }

        this.getCanvasDimensions();

        const maxDimension = this.getDimensionsAndMargins();

        const controllerPromise = this.controller.setupGame(this.dotsWidth, this.dotsHeight, maxDimension, this.imageSrc, IMG_GAME_ROOT);

        this.initializeQuiz();

        const setupPromises: Promise<unknown>[] = [controllerPromise, DotsConnector.loadFonts()];
        // if (this.hintImageSrc) {
        //     setupPromises.push(ImgUtils.loadImageOnCanvas(this.hintImageSrc, maxDimension, true));
        // }
        if (this.style?.sprites?.length) {
            setupPromises.push(ImgUtils.loadImage(IMG_STYLES_ROOT + this.style.sprites[0].file));
        }
        if (this.isSignLanguage()) {
            setupPromises.push(DotsPainter.loadSingLanguageFont());
        }
        if (this.style?.successParticles?.sprite?.file) {
            setupPromises.push(ImgUtils.loadImage(this.style?.successParticles?.sprite?.file));
        }
        setupPromises.push(...this.board.getLoadingPromises());
        // Howler doesn't implement promises, but it is probably a good place to initialize
        setupPromises.push(this.loadSounds());
        setupPromises.push(Firework.loadSounds());
        await Promise.all(setupPromises);

        // For the time being do not load hint image
        // if (this.hintImageSrc) {
        //     this.hintImage = value[4];
        //     this.resetHintImage();
        // }

        await this.initializeDotsElementCollection();

        if (this.magnifierCanvas) {
            this.controller.initializeMagnifier(this.magnifierCanvas, this.getMagnifierSize(), this.magnifierScale, this.style.magnifierColor);
        }

        this.setLineStyle();

        this.board.draw(false);
    }
    getActualDotRadius() {
        const dotElem = this.dotsCollectionElem.get(0) as DotElement;
        if (!dotElem) {
            console.warn("No dot element found to calculate radius");
            return 0;
        }
        return dotElem.getDotRect().width / 2;
    }

    private getCanvasDimensions() {
        this.board.initializeCanvasSizes();
        this.board.resetTransform();

        this.canvasWidth = this.board.layers[0].canvasWidth;
        this.canvasHeight = this.board.layers[0].canvasHeight;
    }

    protected async initializeDotsElementCollection() {
        await super.initializeDotsElementCollection();
        if (this.playing) {
            await this.initializeVariablesBeforeGameStart();
        }
    }

    private async initializeVariablesBeforeGameStart() {
        this.linesCollection.clear();
        this.linesCompleted = -1;
        this.controller.beforeStart();
        this.controller.canDrag = this.style.canDrag && !this._isQuiz;
        if (this.board.isTouchDevice) {
            this.board.mouseDownAdditionalError =
                this.styleIndex == GameStyles.Classic ? DOT_HIT_EXPANSION_TOUCH_MIN - this.getActualDotRadius() : DOT_HIT_EXPANSION_TOUCH;
        } else {
            this.board.mouseDownAdditionalError =
                this.styleIndex == GameStyles.Classic ? Math.max(0, DOT_HIT_EXPANSION_MOUSE_MIN - this.getActualDotRadius()) : 0;
        }
        for (const dotElem of this.dotsCollectionElem) {
            (dotElem as DotElement).hitTargetType = this._isQuiz ? DotHitTargetType.DotAndText : DotHitTargetType.Dot;
        }

        const loadingPromises = this.board.getLoadingPromises();
        await Promise.all(loadingPromises);

        this.setActiveItems();
    }

    private setActiveItems(isEditMode = false) {
        if (this._isQuiz) {
            if (!isEditMode) {
                this.activeNextQuizQuestion();
            }
            for (const dotElem of this.dotsCollectionElem) {
                if (dotElem instanceof DotElement) {
                    if (isEditMode) {
                        dotElem.status = AnimDotStatus.Inactive;
                    }
                    if (this._quizMode == QuizMode.MultipleChoice) {
                        // Activate only the next answers
                        if (dotElem.sourceIndex == this.linesCompleted + 1) {
                            dotElem.reenable(DotHitTargetType.DotAndText);
                        }
                        if (dotElem.sourceIndex != this.linesCompleted + 1) {
                            dotElem.setDisable(false, false, dotElem.sourceIndex <= this.linesCompleted);
                        }
                    }
                }
            }
        } else if (!this.style?.canDrag) {
            for (const dotElem of this.dotsCollectionElem) {
                if (dotElem instanceof DotElement) {
                    dotElem.status =
                        dotElem.hitTarget &&
                        dotElem.sourceIndex - 1 <= this.linesCompleted + Math.min(this.linesCompleted + 1, 7) &&
                        dotElem.sourceIndex - 1 >= this.linesCompleted
                            ? AnimDotStatus.Active
                            : dotElem.sourceIndex - 1 <= this.linesCompleted
                            ? AnimDotStatus.Revealed
                            : AnimDotStatus.Inactive;
                }
            }
        }
    }
    async activeNextQuizQuestion() {
        if (this.questions[this.linesCompleted + 1]?.length) {
            const questionBottom = await this.quizQuestionElement.showQuestion(this.linesCompleted + 1);
            if (questionBottom !== undefined && this._quizMode == QuizMode.MultipleChoice) {
                this.zoomToNextAnswers(questionBottom);
            }
        }
    }

    private zoomToNextAnswers(questionBottom: number) {
        const dotsElements = this.dotsCollectionElem
            .all()
            .map((n) => n as DotElement)
            .filter((n) => n.sourceIndex == this.linesCompleted + 1);
        if (dotsElements.length) {
            const answersRect = dotsElements.reduce(
                (rect: Rect | undefined, elem: DotElement) =>
                    rect ? rect.add(elem.getDotRect()).add(elem.getTextRect()) : elem.getDotRect().clone().add(elem.getTextRect()),
                undefined
            )!;
            const newScale = MathX.between(
                Math.min((this.board.canvasWidth * 0.8) / answersRect.width, (this.board.canvasHeight * 0.8 - questionBottom) / answersRect.height),
                this.board.options.minScale ?? 1,
                Math.min(this.board.options.maxScale ?? MAX_ZOOM_QUIZ_ANSWER * this.board.pixelRatio, MAX_ZOOM_QUIZ_ANSWER * this.board.pixelRatio)
            );

            const finalState = this.board.getScaleAndOriginForNewScale(newScale);

            this.board.ensureRectInsideOfViewportAfterRescale(finalState, answersRect, questionBottom);

            this.board.startCustomAnimation(
                new ClassAnimation(
                    this.board,
                    "",
                    500,
                    {
                        scale: this.board.scale,
                        originX: this.board.originX,
                        originY: this.board.originY
                    },
                    finalState,
                    "easeInOutQuad",
                    1000
                )
            );
        }
    }

    private start() {
        this.initializeVariablesBeforeGameStart();
        this.board.start();
    }

    public stop() {
        this.board.stop();
        this.board.scale = this.board.options.minScale ?? 1;
        this.linesCollection.clear();
        this.linesCompleted = -1;
        if (this.quizQuestionElement) {
            this.quizQuestionElement.hide();
        }
        this.setActiveItems(true);
        this.board.draw(false);
    }

    public get playing(): boolean {
        return this.board.running;
    }

    public draw() {
        return this.board.draw(false);
    }

    private setLineStyle() {
        this.lineStyle = GAME_LINE_SHAPE_STYLE.clone();
        if (this.style?.lineStyle) {
            AnimationHelper.copyMatchingKeyValues(this.style?.lineStyle, this.lineStyle);
        }
    }

    /**
     * Returns the double of the biggest difference between 2 consecutive points
     * @returns Distance in the canvas coordinates
     */
    getDoubleMaxDistance(minDistance: number): number {
        let res = minDistance;
        for (let i = 0; i < this.dotsCollectionElem.length; i++) {
            const current = this.dotsCollectionElem.get(i);
            const next = this.dotsCollectionElem.get((i + 1) % this.dotsCollectionElem.length);
            if (current && next) {
                const distance = Point.PointDistance(current.getPosition(), next.getPosition()) * 2;
                if (distance > res) {
                    res = distance;
                }
            }
        }
        return res;
    }

    /**** DRAG AND DROP ****/

    private mouseDown(pos: Point, hit: CanvasElement | undefined): boolean {
        if (!this.dots.length || !hit || !(hit instanceof DotElement)) {
            return false;
        }

        const dotElement = hit as DotElement;
        if (!dotElement) {
            return false;
        }
        let res = false;

        // hit the next point
        if (!dotElement.isCorrect) {
            this.hitError(dotElement);
            return false;
        }
        if (this.linesCompleted == dotElement.sourceIndex - 1) {
            dotElement.selected = true;
            if (this._isQuiz && this.quizQuestionElement) {
                this.quizQuestionElement.playCorrectSound();
            }

            this.updatePath();
            res = true;
        }
        // hit the current point and start the drag
        if (this.linesCompleted == dotElement.sourceIndex) {
            this.selectNextElement(dotElement);

            if (this.controller.canDrag) {
                this.controller.startDrag(pos);
                this.updatePath(pos);
            }

            res = true;
        }
        if (!res) {
            this.hitError(dotElement);
        }

        return res;
    }

    private hitError(dotElement: DotElement) {
        this.controller.hitError(
            dotElement.x,
            dotElement.y,
            this.styleIndex != GameStyles.Halloween && !Utils.isMobileOrTablet() && !this._isQuiz,
            this._dotRadius
        );
    }

    private selectNextElement(dotElement: DotElement) {
        if (this.linesCompleted > 0) {
            const previousElements = this.dotsCollectionElem.all().filter((n) => (n as DotElement).sourceIndex == dotElement.sourceIndex - 1);
            if (previousElements && previousElements.length) {
                for (const elem of previousElements) {
                    (<DotElement>elem).selected = false;
                    (<DotElement>elem).z = -1;
                }
            }
        }

        dotElement.selected = true;
        dotElement.z = this.style.canDrag ? 1 : -1;
        if (dotElement.index < this.dotsCollectionElem.length) {
            // Move the next element to the top z hit
            const next = this.dotsCollectionElem.find((n) => (<DotElement>n).sourceIndex == dotElement.sourceIndex + 1 && (<DotElement>n).isCorrect);
            if (next) {
                next.z = 2;
            }
        }
        if (dotElement.index > 0) {
            // Flag the next previous element as not longer hitable
            this.disableDot(dotElement.sourceIndex - 1, false, true);
        }
    }

    private disableDot(sourceIndex: number, gameOver: boolean, revealed: boolean) {
        const dots = this.dotsCollectionElem.all().filter((n) => (n as DotElement).sourceIndex == sourceIndex);
        if (dots && dots.length) {
            for (const dot of dots) {
                (dot as DotElement).setDisable(gameOver, revealed);
                if (gameOver && GAME_STYLES[this.styleIndex].sprites?.length) {
                    (dot as DotElement).hideSprite();
                }
            }
        }
    }

    private mouseMove(pos: Point): boolean {
        if (!this.dots.length) {
            return false;
        }
        if (!this.controller.dragMove(pos)) {
            return false;
        }
        if (this.linesCompleted < this.dots.length - 1) {
            this.updatePath(pos);
        }

        // Check if we are over the next point (without and touch error expansion)
        const hit = this.board.getElementAtPos(pos, 0);

        if (hit && hit instanceof DotElement) {
            if ((<DotElement>hit).sourceIndex == this.linesCompleted + 1 && (<DotElement>hit).isCorrect) {
                this.updatePath();
                if (!this.controller.finished) {
                    this.selectNextElement(hit);
                }
                if (this.linesCompleted == this.dots.length - 1) {
                    this.controller.endDrag(true);
                }
            } else if ((<DotElement>hit).sourceIndex > this.linesCompleted + 1 || !(<DotElement>hit).isCorrect) {
                this.mouseUp();
                this.controller.endDrag(false);
                this.hitError(hit);
            }
        }
        return true;
    }

    private mouseUp(): boolean {
        this.controller.hideMagnifier();
        if (this.controller.isDragging()) {
            this.controller.endDrag(false);
            if (this.linesCollection.length > this.linesCompleted - 1 && !this.controller.finished) {
                this.linesCollection.remove(this.linesCollection.length - 1);
            }
            return true;
        }
        return false;
    }

    private updatePath(draggingPos?: Point) {
        const currentDot = this.dots[Math.max(this.linesCompleted, 0)];
        if (draggingPos && this.controller.isDragging()) {
            // We are dragging a point
            if (this.linesCollection.length <= this.linesCompleted) {
                this.linesCollection.add(
                    new LineElement(currentDot.x, currentDot.y, draggingPos.x, draggingPos.y, this.lineStyle, false, "line" + this.linesCompleted)
                );
            } else {
                const currentLine = this.linesCollection.get(this.linesCompleted) as LineElement;
                currentLine.setLine([currentDot.x, currentDot.y, draggingPos.x, draggingPos.y]);
            }
        } else {
            // We just hit the next point
            this.linesCompleted++;
            const nextDot = this.dots[this.linesCompleted];
            if (this.linesCompleted > 0) {
                if (this.linesCollection.length < this.linesCompleted) {
                    // We clicked on the next point
                    const newLine = new LineElement(
                        currentDot.x,
                        currentDot.y,
                        currentDot.x,
                        currentDot.y,
                        this.lineStyle,
                        false,
                        "line" + this.linesCompleted
                    );
                    this.linesCollection.add(newLine);
                    newLine.animate("", 100, { x2: nextDot.x, y2: nextDot.y }, "easeIn", ResetAnimationEnum.All);
                } else {
                    // We dragged into the next point
                    const currentLine = this.linesCollection.get(this.linesCompleted - 1) as LineElement;
                    currentLine.setLine([currentDot.x, currentDot.y, nextDot.x, nextDot.y]);
                }
            }

            if (this.linesCompleted == this.dots.length - 1) {
                this.endGame();
            } else {
                this.stepSolved();
                const volume = 0.1 + (this.linesCompleted / this.dots.length) * 0.9;
                this.controller.playRandomSound("correct", volume);
            }
        }
    }

    private stepSolved() {
        this.setActiveItems();
        window.queueMicrotask(() => {
            try {
                if (this.onSolvedStep) {
                    this.onSolvedStep();
                }
            } catch (e) {
                console.warn("Error calling onSolveStep " + e);
            }
        });
    }

    private endGame() {
        try {
            if (this.controller.effectAboveLayer) {
                this.controller.effectAboveLayer.customClean = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
                    ctx.globalAlpha = 0.3;
                    ctx.globalCompositeOperation = "destination-out";
                    ctx.fillRect(0, 0, w, h);
                    ctx.globalCompositeOperation = "lighter";
                    ctx.globalAlpha = 1;
                };
            }
        } catch (err) {
            console.warn("Error setting effect layer custom clean");
        }

        this.controller.endGame();
        this.startGameOverAnimation();
        this.mouseUp();
        if (this.quizQuestionElement) {
            this.quizQuestionElement.hide();
        }
    }

    private startGameOverAnimation() {
        this.controller.playFireworks(
            this.dotsCollectionElem.all(),
            100,
            (index: number, dot: SizedCanvasElement) => {
                // Disable the dot (just in case). The last one for sure is not disabled yet
                this.disableDot(index, true, true);
                return dot instanceof DotElement && dot.isCorrect;
            },
            this.onGameOverAnimFinished
        );
        for (const line of this.linesCollection) {
            line.animate("opacity", 1000, 0.3, "linear");
        }
        this.mouseUp();
    }

    private async loadSounds() {
        await this.controller.loadSounds(
            this.style?.sounds?.correct,
            this.style?.sounds?.error ?? BRASS_FAIL_SOUNDS,
            this.style?.sounds?.end ?? ["applause.mp3"]
        );
    }

    setBackground(color: string) {
        this.board.backgroundColor = color;
    }
}
